// src/shared/dashboard/widgets/AssignedCourseHeroWidget.tsx
//
// The dashboard hero for a member whose workspace assigned them a course: the
// banner, the progress CTA, and a three-week window onto the course's practice
// steps. Everything is read from the assigned course — its steps, its start
// date, its release schedule — so any organisation's course renders here, not
// just the one this widget was originally written for.
import { ArrowRight, ArrowUpRight, Lock } from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useAuth } from "@/shared/contexts/AuthContext";
import { isInstructor } from "@/shared/lib/instructorAccess";
import { CourseCover } from "@/shared/components/CourseCover";
import {
  useCourseProgress,
  type CourseStep,
} from "@/shared/hooks/useCourseProgress";
import { CRISIS_PROGRAM_COURSE_ID } from "@/shared/lib/cohortCourse";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { useCourseSyllabus } from "@/shared/hooks/useCourseSyllabus";
import { isWeekReleased, weekStartDate } from "@/shared/lib/courseSyllabus";
import { stepDecks } from "@/shared/lib/stepDecks";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

const HERO_IMAGE_URL =
  "https://images.unsplash.com/photo-1453873531674-2151bcd01707?auto=format&fit=crop&w=1600&q=70";
const NAVY = "#0c1a33";
const GOLD = "#D4AF37";
const CARD_COUNT = 3;

const COPY = {
  en: {
    eyebrow: "CRISIS NEGOTIATIONS · FALL 2026",
    headline: "Crisis Negotiations Course",
    intro:
      "A 16-week field program in active listening, incident assessment, influence, and behavioral change.",
    start: "Start the course",
    continue: "Continue the course",
    review: "Review the course",
    starts: (date: string) => `Starts ${date}`,
    modules: (n: number) => `${n} course modules · released weekly`,
    fullProgram: "View full program",
    week: (n: number) => `Week ${n}`,
    locked: "Locked",
    complete: "Complete",
    opens: (date: string) => `Opens ${date}`,
  },
  uk: {
    eyebrow: "КРИЗОВІ ПЕРЕГОВОРИ · ОСІНЬ 2026",
    headline: "Курс кризових переговорів",
    intro:
      "16-тижнева практична програма з активного слухання, оцінки інцидентів, впливу та зміни поведінки.",
    start: "Почати курс",
    continue: "Продовжити курс",
    review: "Переглянути курс",
    starts: (date: string) => `Початок ${date}`,
    modules: (n: number) => `${n} модулів · щотижневе відкриття`,
    fullProgram: "Уся програма",
    week: (n: number) => `Тиждень ${n}`,
    locked: "Заблоковано",
    complete: "Завершено",
    opens: (date: string) => `Відкриється ${date}`,
  },
  pl: {
    eyebrow: "NEGOCJACJE KRYZYSOWE · JESIEŃ 2026",
    headline: "Kurs negocjacji kryzysowych",
    intro:
      "16-tygodniowy program praktyczny: aktywne słuchanie, ocena incydentu, wpływ i zmiana zachowania.",
    start: "Rozpocznij kurs",
    continue: "Kontynuuj kurs",
    review: "Przejrzyj kurs",
    starts: (date: string) => `Start ${date}`,
    modules: (n: number) => `${n} modułów · co tydzień`,
    fullProgram: "Cały program",
    week: (n: number) => `Tydzień ${n}`,
    locked: "Zablokowane",
    complete: "Ukończone",
    opens: (date: string) => `Otwarcie ${date}`,
  },
  ro: {
    eyebrow: "NEGOCIERI ÎN CRIZĂ · TOAMNA 2026",
    headline: "Curs de negocieri în criză",
    intro:
      "Un program practic de 16 săptămâni despre ascultare activă, evaluarea incidentelor, influență și schimbare comportamentală.",
    start: "Începe cursul",
    continue: "Continuă cursul",
    review: "Revezi cursul",
    starts: (date: string) => `Începe ${date}`,
    modules: (n: number) => `${n} module · lansate săptămânal`,
    fullProgram: "Programul complet",
    week: (n: number) => `Săptămâna ${n}`,
    locked: "Blocat",
    complete: "Finalizat",
    opens: (date: string) => `Se deschide ${date}`,
  },
} as const;

const LOCALE_TAG: Record<string, string> = {
  en: "en-GB",
  uk: "uk-UA",
  pl: "pl-PL",
  ro: "ro-RO",
};

function openPath(path: string) {
  window.history.pushState({}, "", path);
  (window as any).navigate?.(path);
}

function formatReleaseDate(date: Date, locale: string) {
  return new Intl.DateTimeFormat(LOCALE_TAG[locale] ?? LOCALE_TAG.en, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}

function WeekCard({
  courseId,
  week,
  title,
  duration,
  releaseDate,
  locked,
  complete,
  coverUrl,
  copy,
}: {
  courseId: string;
  week: number;
  title: string;
  duration?: string;
  releaseDate?: string;
  locked: boolean;
  complete: boolean;
  coverUrl?: string;
  copy: (typeof COPY)[keyof typeof COPY];
}) {
  const meta = [
    copy.week(week),
    duration,
    locked
      ? releaseDate
        ? copy.opens(releaseDate)
        : copy.locked
      : complete
        ? copy.complete
        : null,
  ]
    .filter(Boolean)
    .join("  ·  ");

  return (
    <button
      type="button"
      disabled={locked}
      onClick={() => openPath(`/course/${courseId}/week/${week}`)}
      aria-label={`${copy.week(week)}: ${title}`}
      className={
        "group flex flex-col text-left border p-4 sm:p-5 transition-colors duration-200 " +
        (locked
          ? "border-white/20 bg-[#4b5563] cursor-not-allowed"
          : "border-white/15 bg-[#0a1730]/75 hover:bg-[#122142]/85 cursor-pointer")
      }
    >
      <div className="flex items-center justify-between">
        <span
          className="ed-mono text-[10px]"
          style={{ color: locked ? "rgba(255,255,255,0.68)" : GOLD }}
        >
          /WK {String(week).padStart(2, "0")}
        </span>
        {locked ? (
          <Lock size={13} className="text-white/65" aria-label={copy.locked} />
        ) : (
          <ArrowUpRight
            size={15}
            className="text-white/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        )}
      </div>

      <div
        className={`mt-3 h-[150px] w-full overflow-hidden ${locked ? "grayscale brightness-75" : ""}`}
      >
        <CourseCover
          id={`${courseId}-w${week}`}
          slidesUrl={coverUrl}
          courseName={title.split("—")[1]?.trim() ?? title}
          className="h-full w-full object-cover"
        />
      </div>
      <h3
        className={`ed-serif mt-3 text-[17px] leading-snug ${locked ? "text-white/80" : "text-white"}`}
      >
        {title}
      </h3>
      <p
        className={`ed-mono mt-3 text-[9.5px] ${locked ? "text-white/65" : "text-white/45"}`}
      >
        {meta}
      </p>
      {!locked && (
        <div className="mt-3 h-px w-full bg-white/15">
          <div
            className="h-px transition-all duration-700"
            style={{ width: complete ? "100%" : "0%", backgroundColor: GOLD }}
          />
        </div>
      )}
    </button>
  );
}

function AssignedCourseHeroWidgetImpl(_: WidgetProps) {
  const { locale } = useLanguage();
  const copy = COPY[locale as keyof typeof COPY] ?? COPY.en;
  // The workspace's assigned course, so this hero belongs to whichever course a
  // member was given rather than to one hardcoded program.
  const { defaultCourseId } = useVertical();
  const courseId = defaultCourseId || CRISIS_PROGRAM_COURSE_ID;
  const { steps, loading, isStepCompleted } = useCourseProgress(courseId);
  const { schedule, weeks } = useCourseSyllabus(courseId);
  const today = new Date();
  // Instructors (Scott's accounts) see the whole program unlocked — same bypass
  // as the course page and the week pages.
  const { user } = useAuth();
  const instructorPreview = isInstructor(user?.email);
  const totalWeeks = weeks.length || steps.length;
  const weekReleased = (week: number) =>
    instructorPreview || isWeekReleased(schedule, week, totalWeeks, today);
  const releaseDateOf = (week: number) => {
    const start = weekStartDate(schedule, week, totalWeeks);
    return start ? formatReleaseDate(start, locale) : undefined;
  };

  // The course's practice steps, in week order — the spine this hero previews.
  const practiceSteps = steps
    .filter((step) => step.step_type !== "lesson" && step.week_number != null)
    .sort((a, b) => (a.week_number ?? 0) - (b.week_number ?? 0));

  const isComplete = (step: (typeof practiceSteps)[number]) =>
    isStepCompleted(step.step_number);
  const released = practiceSteps.filter((step) =>
    weekReleased(step.week_number as number),
  );
  const nextStep =
    released.find((step) => !isComplete(step)) ?? released.at(-1) ?? null;
  const anyCompleted = released.some(isComplete);
  const allReleasedComplete = released.length > 0 && released.every(isComplete);

  // Three-card window: the current week and what follows, kept filled near the end.
  const currentIndex = nextStep ? practiceSteps.indexOf(nextStep) : 0;
  const start = Math.min(
    Math.max(0, currentIndex),
    Math.max(0, practiceSteps.length - CARD_COUNT),
  );
  const heroSteps = practiceSteps.slice(start, start + CARD_COUNT);

  const courseStart = releaseDateOf(1) ?? "";
  const ctaLabel = loading
    ? "…"
    : !nextStep
      ? copy.starts(courseStart)
      : allReleasedComplete
        ? copy.review
        : anyCompleted
          ? copy.continue
          : copy.start;

  return (
    <section
      className="relative w-full overflow-hidden flex flex-col min-h-[560px] md:min-h-[min(calc(100dvh-240px),860px)]"
      style={{
        border: `1px solid ${NAVY}66`,
        backgroundImage: `linear-gradient(115deg, rgba(8,18,40,0.95) 0%, rgba(8,18,40,0.87) 45%, rgba(8,18,40,0.68) 100%), url('${HERO_IMAGE_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 py-10 sm:px-12 sm:py-12">
        <p
          className="ed-mono inline-flex items-center gap-2 text-[10.5px]"
          style={{ color: GOLD }}
        >
          <span
            aria-hidden
            className="inline-block w-[2px] h-3"
            style={{ backgroundColor: GOLD }}
          />
          {copy.eyebrow}
        </p>
        <h1
          className="ed-serif text-balance mt-4 max-w-[18ch] text-white"
          style={{ fontSize: "clamp(2.1rem, 4.5vw, 3.4rem)" }}
        >
          {copy.headline}
        </h1>
        <p className="mt-4 max-w-[58ch] text-[14.5px] sm:text-[15px] leading-[1.6] text-white/75">
          {copy.intro}
        </p>
        <div className="mt-8">
          <button
            type="button"
            disabled={loading || !nextStep}
            onClick={() =>
              nextStep &&
              openPath(`/course/${courseId}/week/${nextStep.week_number}`)
            }
            className="group inline-flex items-center gap-2 ed-display text-[14px] px-6 py-3 bg-white transition-colors duration-200 hover:bg-stone-100 active:scale-[0.98] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            style={{ color: NAVY }}
          >
            {ctaLabel}
            {nextStep && (
              <ArrowRight
                size={15}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            )}
          </button>
        </div>
      </div>

      <div className="relative z-10 px-6 pb-6 sm:px-12 sm:pb-10">
        <div className="flex items-baseline justify-between gap-4 border-t border-white/15 pt-5 mb-4">
          <span className="ed-mono text-[10px] text-white/55">
            {copy.modules(practiceSteps.length)}
          </span>
          <button
            type="button"
            onClick={() => openPath(`/course/${courseId}`)}
            className="group inline-flex items-center gap-1.5 ed-mono text-[10px] text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            {copy.fullProgram}
            <ArrowRight
              size={11}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {heroSteps.map((step) => {
            const week = step.week_number as number;
            return (
              <WeekCard
                key={step.id}
                courseId={courseId}
                week={week}
                title={step.title}
                duration={
                  step.estimated_duration_minutes
                    ? `${step.estimated_duration_minutes} min`
                    : undefined
                }
                releaseDate={releaseDateOf(week)}
                locked={!weekReleased(week)}
                complete={isComplete(step)}
                coverUrl={stepDecks(step)[0]}
                copy={copy}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

registerWidget({
  id: "assignedCourseHero",
  component: AssignedCourseHeroWidgetImpl,
  supportedSlots: new Set(["hero"]),
  displayName: "Crisis Program Hero",
});

export const AssignedCourseHeroWidget = AssignedCourseHeroWidgetImpl;
